import React from 'react'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { clsx } from 'clsx'
import { PaginationInfo } from '../../types'

interface PaginationProps {
  pagination: PaginationInfo | any | undefined
  onPageChange: (page: number) => void
  showInfo?: boolean
  showSizeSelector?: boolean
  pageSizes?: number[]
  onPageSizeChange?: (size: number) => void
}

const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  showInfo = true,
  showSizeSelector = true,
  pageSizes = [10, 25, 50, 100],
  onPageSizeChange
}) => {
  // Verificação de segurança para evitar erro de destructuring
  if (!pagination) {
    return null
  }

  // Normalizar dados da paginação para lidar com diferentes formatos da API
  const normalizedPagination = {
    page: (pagination as any).currentPage || pagination.page || 1,
    limit: (pagination as any).itemsPerPage || pagination.limit || 25,
    total: (pagination as any).totalItems || pagination.total || 0,
    totalPages: pagination.totalPages || 1,
    hasNext: pagination.hasNext || false,
    hasPrev: pagination.hasPrev || false
  }

  const { page, limit, total, totalPages, hasNext, hasPrev } = normalizedPagination

  // Gerar array de páginas para mostrar
  const getPageNumbers = () => {
    const delta = 2 // Número de páginas para mostrar em cada lado da página atual
    const range = []
    const rangeWithDots = []

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i)
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, 'dots')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (page + delta < totalPages - 1) {
      rangeWithDots.push('dots', totalPages)
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  const pageNumbers = getPageNumbers()

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value)
    onPageSizeChange?.(newSize)
  }

  // Calcular range dos itens mostrados
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1
  const endItem = Math.min(page * limit, total)

  if (totalPages <= 1 && !showInfo && !showSizeSelector) {
    return null
  }

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      {/* Informações (mobile e desktop) */}
      {showInfo && (
        <div className="flex-1 flex justify-between sm:hidden">
          <p className="text-sm text-gray-700">
            Mostrando {startItem} a {endItem} de {total} resultados
          </p>
        </div>
      )}

      {/* Desktop */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        {/* Informações e seletor de tamanho */}
        <div className="flex items-center space-x-4">
          {showInfo && (
            <p className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{startItem}</span> a{' '}
              <span className="font-medium">{endItem}</span> de{' '}
              <span className="font-medium">{total}</span> resultados
            </p>
          )}

          {showSizeSelector && onPageSizeChange && (
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-700">Itens por página:</label>
              <select
                value={limit}
                onChange={handlePageSizeChange}
                className="rounded border-gray-300 text-sm focus:border-primary-500 focus:ring-primary-500"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Navegação */}
        {totalPages > 1 && (
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            {/* Botão Anterior */}
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={!hasPrev}
              className={clsx(
                'relative inline-flex items-center px-2 py-2 rounded-l-md border text-sm font-medium',
                hasPrev
                  ? 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
                  : 'bg-gray-100 border-gray-300 text-gray-300 cursor-not-allowed'
              )}
            >
              <ChevronLeft className="h-5 w-5" />
              <span className="sr-only">Anterior</span>
            </button>

            {/* Números das páginas */}
            {pageNumbers.map((pageNumber, index) => {
              if (pageNumber === 'dots') {
                return (
                  <span
                    key={`dots-${index}`}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                  >
                    <MoreHorizontal className="h-5 w-5" />
                  </span>
                )
              }

              const isCurrentPage = pageNumber === page

              return (
                <button
                  key={pageNumber}
                  onClick={() => onPageChange(pageNumber as number)}
                  className={clsx(
                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500',
                    isCurrentPage
                      ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  )}
                >
                  {pageNumber}
                </button>
              )
            })}

            {/* Botão Próximo */}
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={!hasNext}
              className={clsx(
                'relative inline-flex items-center px-2 py-2 rounded-r-md border text-sm font-medium',
                hasNext
                  ? 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
                  : 'bg-gray-100 border-gray-300 text-gray-300 cursor-not-allowed'
              )}
            >
              <ChevronRight className="h-5 w-5" />
              <span className="sr-only">Próximo</span>
            </button>
          </nav>
        )}
      </div>

      {/* Mobile - apenas navegação simples */}
      {totalPages > 1 && (
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={!hasPrev}
            className={clsx(
              'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md',
              hasPrev
                ? 'bg-white text-gray-700 hover:bg-gray-50'
                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
            )}
          >
            Anterior
          </button>
          
          <span className="text-sm text-gray-700 flex items-center">
            Página {page} de {totalPages}
          </span>

          <button
            onClick={() => onPageChange(page + 1)}
            disabled={!hasNext}
            className={clsx(
              'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md',
              hasNext
                ? 'bg-white text-gray-700 hover:bg-gray-50'
                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
            )}
          >
            Próximo
          </button>
        </div>
      )}
    </div>
  )
}

export default Pagination